import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { TitleBar } from './components/chrome/TitleBar';
import { TopNav } from './components/chrome/TopNav';
import { ProjectSidebar } from './components/chrome/ProjectSidebar';
import { ToolRail } from './components/chrome/ToolRail';
import { ValidationSidebar } from './components/chrome/ValidationSidebar';
import { ToolPanel } from './components/panels/ToolPanel';
import { ModelViewport } from './components/viewport/ModelViewport';
import { ViewportControls } from './components/viewport/ViewportControls';
import { SummaryCard } from './components/SummaryCard';
import { ConfirmDialog } from './components/ui/ConfirmDialog';
import { HistoryPanel } from './components/history/HistoryPanel';
import { useProjectHistory } from './hooks/useProjectHistory';
import { describeEvent, timeLabel } from './utils/historyText';
import { modelName } from './data/model';
import { getPartNames, getSelectedPart, subscribeViewportBridge } from './utils/viewportBridge';
import type { SupportState, ToolId, ViewMode } from './types/tools';
import type { PrepareMode, PrepareRequest, PrepareStep } from './types/prepare';
import type { ProtectAiResult, ProtectCommand, ProtectCommandType, ProtectSettings } from './types/protect';
import type { HistoryEntry, ModelSnapshot, RestoreRequest, ViewportHistoryEvent } from './types/history';
type AppProps = {
  initialTool?: 'none' | 'support' | 'protect' | 'orient' | 'layout' | 'hollow' | 'texture' | 'label' | 'hole' | 'size' | 'oneclick';
  modelAppearance?: 'textured' | 'material';
};
export function App({
  initialTool = 'none',
  modelAppearance = 'textured'
}: AppProps) {
  const [cameraResetKey, setCameraResetKey] = useState(0);
  const [activeTool, setActiveTool] = useState<ToolId | null>(initialTool === 'none' ? null : initialTool);
  const [view, setView] = useState<ViewMode>(initialTool === 'layout' ? 'top' : 'perspective');
  const [supportState, setSupportState] = useState<SupportState>('none');
  const [prepareRequest, setPrepareRequest] = useState<PrepareRequest | null>(null);
  const [prepareStep, setPrepareStep] = useState<PrepareStep>('idle');
  const [touchpoints, setTouchpoints] = useState(0);
  const [manualPlacement, setManualPlacement] = useState(false);
  const [protectSettings, setProtectSettings] = useState<ProtectSettings>({
    tool: 'paint',
    brushSize: 5,
    mode: 'protected'
  });
  const [protectCommand, setProtectCommand] = useState<ProtectCommand | null>(null);
  const [protectDirty, setProtectDirty] = useState(false);
  const [protectAiResult, setProtectAiResult] = useState<ProtectAiResult | null>(null);
  const [pendingTool, setPendingTool] = useState<{
    next: ToolId | null;
  } | null>(null);
  const history = useProjectHistory();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restoreRequest, setRestoreRequest] = useState<RestoreRequest | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<HistoryEntry | null>(null);
  const aiRegionsRef = useRef<number | null>(null);
  const manualRef = useRef(manualPlacement);
  manualRef.current = manualPlacement;

  // History follows the selected model; with nothing selected it shows the original.
  const selectedPart = useSyncExternalStore(subscribeViewportBridge, getSelectedPart);
  const partNames = useSyncExternalStore(subscribeViewportBridge, getPartNames);
  const historyPartId = selectedPart ?? 'main';
  const partEntries = history.entries.filter(e => e.partId === historyPartId);
  const partVersions = history.versions.filter(v => v.partId === historyPartId);
  useEffect(() => {
    setActiveTool(initialTool === 'none' ? null : initialTool);
    setView(initialTool === 'layout' ? 'top' : 'perspective');
  }, [initialTool]);
  const sendProtect = (type: ProtectCommandType) => setProtectCommand({
    id: Date.now() + Math.random(),
    type
  });
  const openTool = (next: ToolId | null) => {
    setActiveTool(next);
    if (next === 'layout') setView('top');
  };

  /** Leaving Protect with unsaved paint asks before discarding. */
  const requestTool = (next: ToolId | null) => {
    if (activeTool === 'protect' && next !== 'protect' && protectDirty) {
      setPendingTool({
        next
      });
      return;
    }
    openTool(next);
  };
  const selectTool = (tool: ToolId) => requestTool(activeTool === tool ? null : tool);
  const runPrepare = (mode: PrepareMode) => {
    if (mode === 'clear') {
      setSupportState('none');
      setPrepareStep('idle');
      setTouchpoints(0);
    } else {
      setSupportState('generating');
      setPrepareStep(mode === 'full' || mode === 'orient' ? 'orienting' : 'supporting');
      if (mode === 'orient') setTouchpoints(0);
      if (view === 'top') setView('perspective');
    }
    setPrepareRequest({
      id: Date.now(),
      mode
    });
  };
  const handleComplete = useCallback((count: number) => {
    setTouchpoints(count);
    setSupportState(count > 0 ? 'generated' : 'none');
    setPrepareStep(count > 0 ? 'done' : 'idle');
  }, []);
  const handleAiDone = useCallback((regions: number) => {
    aiRegionsRef.current = regions;
    setProtectAiResult({
      id: Date.now(),
      regions
    });
  }, []);
  const {
    record,
    truncateTo,
    saveVersion
  } = history;
  const handleHistoryEvent = useCallback((event: ViewportHistoryEvent) => {
    const text = describeEvent(event, {
      modelName,
      manualPlacement: manualRef.current,
      aiRegions: aiRegionsRef.current
    });
    if (event.type === 'protected') aiRegionsRef.current = null;
    record({
      ...text,
      partId: event.partId,
      thumbnail: event.capture.thumbnail,
      snapshot: event.capture.snapshot
    });
  }, [record]);
  const applySnapshot = (snapshot: ModelSnapshot, partId: string) => {
    setRestoreRequest({
      id: Date.now(),
      snapshot,
      partId
    });
    setTouchpoints(snapshot.touchpoints);
    setSupportState(snapshot.hasSupports ? 'generated' : 'none');
    setPrepareStep(snapshot.hasSupports ? 'done' : 'idle');
    setProtectDirty(false);
  };
  const laterSteps = restoreTarget ? history.entries.filter(e => e.partId === restoreTarget.partId).findIndex(e => e.id === restoreTarget.id) : 0;
  const supportsGenerated = supportState === 'generated';
  return <div className="flex h-screen w-full min-w-[1080px] flex-col overflow-hidden bg-pf-canvas font-sans text-pf-ink" data-id="element-0" data-mp="25t5p6zlx00ek">
      <TitleBar data-id="element-1" data-mp="2kpwo77rofa3n" />
      <TopNav data-id="element-2" data-mp="2kpemhvm8w3oc" />
      <div className="flex min-h-0 flex-1" data-id="element-3" data-mp="2kovdx5ihirmz">
        <ProjectSidebar historyOpen={historyOpen} onToggleHistory={() => setHistoryOpen(o => !o)} data-id="element-4" data-mp="2frd65ig7eigk" />
        <AnimatePresence initial={false} data-id="element-5" data-mp="2tii7qq34tktj">
          {historyOpen && <motion.div key="history" initial={{
          width: 0
        }} animate={{
          width: 340
        }} exit={{
          width: 0
        }} transition={{
          duration: 0.26,
          ease: [0.23, 1, 0.32, 1]
        }} className="shrink-0 overflow-hidden border-r border-pf-line bg-white" data-id="element-6" data-mp="2tkum6t9old1u">
            
              <HistoryPanel key={historyPartId} entries={partEntries} versions={partVersions} partName={partNames[historyPartId] ?? 'Model'} onClose={() => setHistoryOpen(false)} onRequestRestore={setRestoreTarget} onSaveVersion={saveVersion} onRestoreVersion={v => {
            applySnapshot(v.snapshot, v.partId);
            record({
              kind: 'restore',
              partId: v.partId,
              title: `Restored ${v.name}`,
              description: `From “${v.title}” at ${timeLabel(v.sourceTime)}`,
              thumbnail: v.thumbnail,
              snapshot: v.snapshot
            });
          }} data-id="element-7" data-mp="3i3mwsqgriz7b" />
            
            </motion.div>}
        </AnimatePresence>
        <main className="relative min-w-0 flex-1 overflow-hidden bg-pf-canvas" data-id="element-8" data-mp="2gfkgdi4wjb2x">
          <ModelViewport view={view} resetKey={cameraResetKey} appearance={modelAppearance} prepareRequest={prepareRequest} onPrepareProgress={setPrepareStep} onPrepareComplete={handleComplete} protectActive={activeTool === 'protect'} protectSettings={protectSettings} protectCommand={protectCommand} onProtectDirtyChange={setProtectDirty} onProtectAiDone={handleAiDone} restoreRequest={restoreRequest} onHistoryEvent={handleHistoryEvent} data-id="element-9" data-mp="2kg4gmukruuta" />
          
          <ViewportControls view={view} onToggleView={() => setView(v => v === 'top' ? 'perspective' : 'top')} onHome={() => {
          setView('perspective');
          setCameraResetKey(k => k + 1);
        }} data-id="element-10" data-mp="2d4rm5b35av8b" />
          
          <div className="pointer-events-none absolute bottom-[6px] left-[6px] top-[6px] flex items-start gap-[8px] [&>*]:pointer-events-auto" data-id="element-11" data-mp="1y91x142ny1zd">
            <ToolRail active={activeTool} onSelect={selectTool} dimmed={manualPlacement && activeTool === 'support'} data-id="element-12" data-mp="1u7qgdo1ror3b" />
            <AnimatePresence mode="wait" data-id="element-13" data-mp="9r8u4j1326wf">
              {activeTool && <motion.div key={activeTool} className="flex max-h-full" initial={{
              opacity: 0,
              x: -6
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -4
            }} transition={{
              duration: 0.14,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-14" data-mp="dowu40hmupv6">
                
                  <ToolPanel tool={activeTool} onClose={() => requestTool(null)} supportState={supportState} prepareStep={prepareStep} touchpoints={touchpoints} onPrepareAll={() => runPrepare('full')} onGenerate={() => runPrepare('supports')} onClear={() => runPrepare('clear')} onAutoOrient={() => runPrepare('orient')} onManualChange={setManualPlacement} protect={{
                settings: protectSettings,
                onSettingsChange: setProtectSettings,
                onDone: () => {
                  sendProtect('save');
                  openTool(null);
                },
                onDeselectAll: () => sendProtect('clear'),
                onRunAi: () => sendProtect('ai'),
                aiResult: protectAiResult
              }} data-id="element-15" data-mp="3r5e0gxogic4v" />
                
                </motion.div>}
            </AnimatePresence>
          </div>
          <SummaryCard supportsGenerated={supportsGenerated} touchpoints={touchpoints} data-id="element-16" data-mp="26etl5jvq2s0t" />
        </main>
        <ValidationSidebar supportsGenerated={supportsGenerated} data-id="element-17" data-mp="2b5jqix45euvi" />
      </div>

      <AnimatePresence data-id="element-18" data-mp="2b7mkfs8f985i">
        {pendingTool && <ConfirmDialog key="protect-confirm" title="Save protected zones?" message="Are you sure you don’t want to save the protected zones? Unsaved changes will be lost." confirmLabel="Save Zones" discardLabel="Don’t Save" onCancel={() => setPendingTool(null)} onDiscard={() => {
        sendProtect('revert');
        openTool(pendingTool.next);
        setPendingTool(null);
      }} onConfirm={() => {
        sendProtect('save');
        openTool(pendingTool.next);
        setPendingTool(null);
      }} data-id="element-19" data-mp="1dqxlq7erqm0c" />}
        {restoreTarget && <ConfirmDialog key="restore-confirm" title="Restore this version?" message={`Go back to “${restoreTarget.title}” from ${timeLabel(restoreTarget.time)}. Restore removes the ${laterSteps} later ${laterSteps === 1 ? 'step' : 'steps'} from the timeline. Save as New keeps your full history and adds this state as a new version.`} confirmLabel="Restore" discardLabel="Save as New" cancelLabel="Cancel" onCancel={() => setRestoreTarget(null)} onConfirm={() => {
        applySnapshot(restoreTarget.snapshot, restoreTarget.partId);
        truncateTo(restoreTarget.id);
        setRestoreTarget(null);
      }} onDiscard={() => {
        applySnapshot(restoreTarget.snapshot, restoreTarget.partId);
        saveVersion(restoreTarget);
        record({
          kind: 'restore',
          partId: restoreTarget.partId,
          title: 'Restored version',
          description: `From “${restoreTarget.title}” at ${timeLabel(restoreTarget.time)}`,
          thumbnail: restoreTarget.thumbnail,
          snapshot: restoreTarget.snapshot
        });
        setRestoreTarget(null);
      }} data-id="element-20" data-mp="389xxvd3uesch" />}
      </AnimatePresence>
    </div>;
}